/* ===================================================================
   useOutbox() + outbox action mutations -- the approval loop (Hopper,
   2026-06-09).

   useOutbox()        -> GET /api/v1/outbox: the pending AI-draft queue.
   useOutboxActions() -> approve / edit / skip mutations.

   HARD PRODUCT RULE #1 (no auto-post): every mutation here fires ONLY
   from an explicit operator click in the UI. There is no automation that
   calls them. Approve and Edit move a draft through the human approval
   gate and record it posted; Skip rejects it (never posted). After any
   action we invalidate the reviews + outbox + dashboard queries so the
   inbox reflects the new state immediately.

   AUTH: api.ts attaches the Bearer rr_ token. useOutbox is disabled until
   a token exists so it never auto-fires a 401 on first paint.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type EscalateResult,
  type OutboxActionResult,
  type OutboxResponse,
  type PrivateMessageResult,
  type RegenerateResult,
  type WriteReplyResult,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { qk } from './queryKeys';

export function useOutbox(
  params: { enabled?: boolean } = {},
): UseQueryResult<OutboxResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.outbox(),
    queryFn: ({ signal }) => api.listOutbox(signal),
    enabled: hasToken && params.enabled !== false,
    staleTime: 30_000,
  });
}

export interface UseOutboxActionsResult {
  approve: (draftId: string) => Promise<OutboxActionResult>;
  edit: (draftId: string, finalResponse: string) => Promise<OutboxActionResult>;
  skip: (draftId: string, notes?: string) => Promise<OutboxActionResult>;
  /** Re-run the AI draft for a review; returns the fresh draft text. */
  regenerate: (draftId: string) => Promise<RegenerateResult>;
  /** Record a private (dry-run) response to the reviewer. */
  privateMessage: (draftId: string, message: string) => Promise<PrivateMessageResult>;
  /** Flag the draft for owner attention (persisted). */
  escalate: (draftId: string, note?: string) => Promise<EscalateResult>;
  /** Write + post a MANUAL reply to a review that has NO AI draft (U6). Fires
      only on the owner's explicit submit; never auto-posts. */
  writeReply: (reviewId: string, text: string) => Promise<WriteReplyResult>;
  /** Update a reply that ALREADY exists on a review (edit-reply). Fires only on
      the owner's explicit "Update reply" click; never auto-posts. */
  editReply: (reviewId: string, text: string) => Promise<WriteReplyResult>;
  /** True while any approve/edit/skip mutation is in flight. */
  isPending: boolean;
  /** True while a regenerate is in flight (so the modal can show "Regenerating..."). */
  isRegenerating: boolean;
  /** The last action error, if any (e.g. for an inline toast). */
  error: Error | null;
}

export function useOutboxActions(): UseOutboxActionsResult {
  const qc = useQueryClient();
  const admin = useAdminView();

  // After any state change, refresh the data the inbox renders from.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.reviews() });
    void qc.invalidateQueries({ queryKey: qk.outbox() });
    void qc.invalidateQueries({ queryKey: qk.dashboardSummary() });
  };

  const approveM = useMutation({
    mutationFn: (draftId: string) => api.approveDraft(draftId),
    onSuccess: invalidate,
  });
  const editM = useMutation({
    mutationFn: (v: { draftId: string; finalResponse: string }) =>
      api.editDraft(v.draftId, v.finalResponse),
    onSuccess: invalidate,
  });
  const skipM = useMutation({
    mutationFn: (v: { draftId: string; notes?: string }) =>
      api.skipDraft(v.draftId, v.notes),
    onSuccess: invalidate,
  });
  // Regenerate rewrites the draft in place (status stays pending) -- refresh
  // so the new text shows on the card/inbox too.
  const regenerateM = useMutation({
    mutationFn: (draftId: string) => api.regenerateDraft(draftId),
    onSuccess: invalidate,
  });
  // Private message + escalate persist a flag/note; they don't change the
  // public draft, but invalidate so escalate badges / state stay fresh.
  const privateM = useMutation({
    mutationFn: (v: { draftId: string; message: string }) =>
      api.privateMessageDraft(v.draftId, v.message),
    onSuccess: invalidate,
  });
  const escalateM = useMutation({
    mutationFn: (v: { draftId: string; note?: string }) =>
      api.escalateDraft(v.draftId, v.note),
    onSuccess: invalidate,
  });
  // U6: manual reply for a draftless review. On success the review is now
  // replied, so refresh the inbox like any other action.
  const writeReplyM = useMutation({
    mutationFn: (v: { reviewId: string; text: string }) =>
      api.writeReply(v.reviewId, v.text),
    onSuccess: invalidate,
  });
  // Edit an existing reply. Same endpoint (edit:true); on success the review's
  // reply text changes, so refresh the inbox like any other action.
  const editReplyM = useMutation({
    mutationFn: (v: { reviewId: string; text: string }) =>
      api.editReply(v.reviewId, v.text),
    onSuccess: invalidate,
  });

  // OPTION A read-only: an operator viewing a client's Reviews inbox can SEE
  // the pending AI drafts but can NEVER act on them. Every action becomes an
  // inert no-op that rejects -- defense-in-depth on top of the real guarantee
  // (Hard Rule #1): there is NO admin approve/edit/skip/regenerate/escalate
  // endpoint on the backend to call, so a client's tenant cannot be mutated
  // through this lane at all.
  if (admin) {
    const blocked = async (): Promise<never> => {
      throw new Error('read_only_admin_view');
    };
    return {
      approve: blocked,
      edit: blocked,
      skip: blocked,
      regenerate: blocked,
      privateMessage: blocked,
      escalate: blocked,
      writeReply: blocked,
      editReply: blocked,
      isPending: false,
      isRegenerating: false,
      error: null,
    };
  }

  return {
    approve: (draftId) => approveM.mutateAsync(draftId),
    edit: (draftId, finalResponse) => editM.mutateAsync({ draftId, finalResponse }),
    skip: (draftId, notes) => skipM.mutateAsync({ draftId, notes }),
    regenerate: (draftId) => regenerateM.mutateAsync(draftId),
    privateMessage: (draftId, message) =>
      privateM.mutateAsync({ draftId, message }),
    escalate: (draftId, note) => escalateM.mutateAsync({ draftId, note }),
    writeReply: (reviewId, text) => writeReplyM.mutateAsync({ reviewId, text }),
    editReply: (reviewId, text) => editReplyM.mutateAsync({ reviewId, text }),
    isPending:
      approveM.isPending || editM.isPending || skipM.isPending ||
      privateM.isPending || escalateM.isPending || writeReplyM.isPending ||
      editReplyM.isPending,
    isRegenerating: regenerateM.isPending,
    error:
      approveM.error ?? editM.error ?? skipM.error ??
      regenerateM.error ?? privateM.error ?? escalateM.error ??
      writeReplyM.error ?? editReplyM.error ?? null,
  };
}
